import { RevenueGridHeader, RevenueGridItem, setArrowIcon } from '@e2m-customer-portal/domains';
import { useRevenueData } from '@e2m-customer-portal/hooks';
import {
  isBattery,
  isCogeneration,
  isCoLocation,
  isControlEnergy,
  isSolar,
  MachineTechnology,
} from '@e2m-customer-portal/shared';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import { ReactElement, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { RevenueItem, RevenueScope } from '../../../generated/graphql';
import { ErrorComponent } from '@e2m-customer-portal/ui';

export interface MonthlyRevenueTableProps {
  marketLocationId: string;
  selectedDate: Date;
  technologies: readonly MachineTechnology[];
  sidebarOffset?: number;
}

export function MonthlyRevenueTable({
  marketLocationId,
  selectedDate,
  technologies,
  sidebarOffset = 0,
}: Readonly<MonthlyRevenueTableProps>): ReactElement<MonthlyRevenueTableProps> {
  const [t] = useTranslation();
  const { data, error } = useRevenueData(RevenueScope.MONTH, marketLocationId, selectedDate);
  const revenueItemsMap = new Map<string, RevenueItem>();

  if (!data || error) {
    return <ErrorComponent error={error} />;
  }

  data.items.map((item) =>
    revenueItemsMap.set(item.key, { key: item.key, amount: item?.amount, price: item?.price, sum: item?.sum }),
  );

  const hasIntradayTrading = Boolean(
    revenueItemsMap.get('RE37')?.amount || revenueItemsMap.get('RE37')?.price || revenueItemsMap.get('RE37')?.sum,
  );

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    RE02: false,
    RE33: false,
    RE30: true,
    RE37: isBattery(technologies) || isCoLocation(technologies),
    RE05: true,
    RE07: true,
    RE09: true,
    RE12: true,
    RE41: true,
    RE42: true,
    RE34: true,
    RE16: isCogeneration(technologies),
    RE27: false,
  });

  const allSectionsOpen =
    (openSections.RE02 ||
      openSections.RE33 ||
      openSections.RE37 === (!isBattery(technologies) && !isCoLocation(technologies)) ||
      openSections.RE16 === !isCogeneration(technologies) ||
      openSections.RE27) &&
    !Object.values(openSections).every(Boolean)
      ? null
      : Object.values(openSections).every(Boolean);

  return (
    <div data-testid="monthly-revenue-grid">
      <RevenueGridHeader
        allSectionsOpen={allSectionsOpen}
        showToggle={!isCogeneration(technologies) && !isSolar(technologies)}
        sidebarOffset={sidebarOffset}
        toggleOpenSections={() =>
          setOpenSections((openSections) => {
            let sections = openSections;
            for (const key of Object.keys(openSections)) {
              if (['RE02', 'RE33', 'RE16', 'RE27'].includes(key)) continue;
              sections = { ...sections, [key]: true };
            }
            sections = {
              ...sections,
              RE02: !allSectionsOpen,
              RE33: !allSectionsOpen,
              RE37: !allSectionsOpen || isBattery(technologies) || isCoLocation(technologies),
              RE16: !allSectionsOpen || isCogeneration(technologies),
              RE27: !allSectionsOpen,
            };
            return sections;
          })
        }
      />
      {!isControlEnergy(technologies) && (
        <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
          <RevenueGridItem
            label={t('REVENUE_REPORT_TABLE_TOTAL_REVENUE')}
            variant="h3"
            className="py-4"
            indentLevel="0"
          />
          <RevenueGridItem
            item={revenueItemsMap.get('RE02')}
            label={t('REVENUE_REPORT_TABLE_CONTRACT_PRICE')}
            variant={isCoLocation(technologies) ? 'body3' : undefined}
            icon={isCoLocation(technologies) ? setArrowIcon(openSections.RE02, technologies) : undefined}
            contextInfo={t(
              isSolar(technologies)
                ? 'REVENUE_REPORT_TABLE_HELP_CONTRACT_PRICE_SOLAR'
                : 'REVENUE_REPORT_TABLE_HELP_CONTRACT_PRICE',
            )}
            isGrey
            onClick={
              isCoLocation(technologies)
                ? () => setOpenSections({ ...openSections, RE02: !openSections.RE02 })
                : undefined
            }
          />
          {isCoLocation(technologies) && (
            <Collapse in={openSections.RE02} className="col-span-5">
              <RevenueGridItem
                item={revenueItemsMap.get('RE46')}
                label={t('MONTHLY_REVENUE_REPORT_TABLE_PV_PRODUCTION')}
                indentLevel="1"
                className="py-4"
                contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_PV_PRODUCTION')}
              />
              <RevenueGridItem
                item={revenueItemsMap.get('RE47')}
                label={t('MONTHLY_REVENUE_REPORT_TABLE_SERVICE_FEE')}
                indentLevel="1"
                className="py-4"
                contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_SERVICE_FEE')}
                isGrey
              />
              <RevenueGridItem
                item={revenueItemsMap.get('RE48')}
                label={t('MONTHLY_REVENUE_REPORT_TABLE_GUARANTEED_PROFIT')}
                indentLevel="1"
                className="py-4"
                contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_GUARANTEED_PROFIT')}
              />
              <RevenueGridItem
                item={revenueItemsMap.get('RE49')}
                label={t('MONTHLY_REVENUE_REPORT_TABLE_NON_AVAILABILITY_PV_NEG_SPOT')}
                indentLevel="1"
                className="py-4"
                contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_NON_AVAILABILITY_PV_NEG_SPOT')}
                isGrey
              />
              <RevenueGridItem
                item={revenueItemsMap.get('RE50')}
                label={t('MONTHLY_REVENUE_REPORT_TABLE_NON_AVAILABILITY_PV_IMBALANCE')}
                indentLevel="1"
                className="py-4"
                contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_NON_AVAILABILITY_PV_IMBALANCE')}
              />
              <RevenueGridItem
                item={revenueItemsMap.get('RE51')}
                label={t('MONTHLY_REVENUE_REPORT_TABLE_NON_AVAILABILITY_PV_BATTERY')}
                indentLevel="1"
                className="py-4"
                contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_NON_AVAILABILITY_PV_BATTERY')}
                isGrey
              />
            </Collapse>
          )}
          <RevenueGridItem
            item={revenueItemsMap.get('RE31')}
            label={t('REVENUE_REPORT_TABLE_REDISPATCH')}
            contextInfo={t('REVENUE_REPORT_TABLE_HELP_REDISPATCH')}
          />
          {!isCoLocation(technologies) && (
            <RevenueGridItem
              item={revenueItemsMap.get('RE03')}
              label={t('REVENUE_REPORT_TABLE_PROPORTIONAL_PROFIT')}
              contextInfo={
                <Trans
                  components={{
                    h4: <h4 className="text-sx font-semibold mb-0">{}</h4>,
                    i: <i />,
                  }}
                >
                  REVENUE_REPORT_TABLE_HELP_PROPORTIONAL_PROFIT
                </Trans>
              }
              isGrey
            />
          )}
        </div>
      )}
      {!isCogeneration(technologies) && (
        <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
          <RevenueGridItem
            label={t('REVENUE_REPORT_TABLE_SINGLE_ITEMS')}
            variant="h3"
            indentLevel="0"
            className="py-4"
          />
          <RevenueGridItem
            item={revenueItemsMap.get('RE33')}
            label={t('REVENUE_REPORT_TABLE_SCHEDULE_MANAGEMENT')}
            contextInfo={t('REVENUE_REPORT_TABLE_HELP_SCHEDULE_MANAGEMENT')}
            icon={setArrowIcon(openSections.RE33, technologies)}
            variant="h5"
            onClick={() => setOpenSections({ ...openSections, RE33: !openSections.RE33 })}
          />
          <Collapse in={openSections.RE33} className="col-span-5">
            <div className="col-span-5 grid border-collapse grid-cols-5 gap-0 transition-all">
              <RevenueGridItem
                item={revenueItemsMap.get('RE30')}
                label={t('REVENUE_REPORT_TABLE_MARKETING_PROCEEDS')}
                contextInfo={t('REVENUE_REPORT_TABLE_HELP_MARKETING_PROCEEDS')}
                icon={setArrowIcon(openSections.RE30, technologies)}
                variant="body3"
                indentLevel="1"
                isGrey
                onClick={() => setOpenSections({ ...openSections, RE30: !openSections.RE30 })}
              />

              <Collapse in={openSections.RE30} className="col-span-5">
                {(isBattery(technologies) || isCoLocation(technologies)) && (
                  <>
                    <RevenueGridItem
                      item={revenueItemsMap.get('RE37')}
                      label={t('MONTHLY_REVENUE_REPORT_TABLE_INTRADAY_TRADING')}
                      contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_INTRADAY_TRADING')}
                      icon={setArrowIcon(openSections.RE37, technologies)}
                      variant="body3"
                      indentLevel="1.5"
                      onClick={() => setOpenSections({ ...openSections, RE37: !openSections.RE37 })}
                    />
                    <Collapse in={openSections.RE37} className="col-span-5">
                      <RevenueGridItem
                        item={revenueItemsMap.get('RE38')}
                        label={t('MONTHLY_REVENUE_REPORT_TABLE_INTRADAY_TRADING_PURCHASE')}
                        contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_INTRADAY_TRADING_PURCHASE')}
                        indentLevel="2"
                        isGrey
                      />
                      <RevenueGridItem
                        item={revenueItemsMap.get('RE39')}
                        label={t('MONTHLY_REVENUE_REPORT_TABLE_INTRADAY_TRADING_SALE')}
                        contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_INTRADAY_TRADING_SALE')}
                        indentLevel="2"
                      />
                    </Collapse>
                  </>
                )}
              </Collapse>

              {isBattery(technologies) || isCoLocation(technologies) ? (
                <>
                  <RevenueGridItem
                    item={revenueItemsMap.get('RE41')}
                    label={t('MONTHLY_REVENUE_REPORT_TABLE_DELIVERY_VALUE_DEVIATION')}
                    contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_DELIVERY_VALUE_DEVIATION')}
                    icon={setArrowIcon(openSections.RE41, technologies)}
                    indentLevel="1"
                    variant="body3"
                    isGrey
                    onClick={() => setOpenSections({ ...openSections, RE41: !openSections.RE41 })}
                  />
                  <Collapse in={openSections.RE41} className="col-span-5">
                    <RevenueGridItem
                      item={revenueItemsMap.get('RE42')}
                      label={t('MONTHLY_REVENUE_REPORT_TABLE_COMPENSATION_ENERGY_TOTAL')}
                      contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_COMPENSATION_ENERGY_TOTAL')}
                      icon={setArrowIcon(openSections.RE42, technologies)}
                      indentLevel="1.5"
                      variant="body3"
                      onClick={() => setOpenSections({ ...openSections, RE42: !openSections.RE42 })}
                    />
                    <Collapse in={openSections.RE42} className="col-span-5">
                      <RevenueGridItem
                        item={revenueItemsMap.get('RE43')}
                        label={t('MONTHLY_REVENUE_REPORT_TABLE_COMPENSATION_ENERGY_PURCHASE')}
                        contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_COMPENSATION_ENERGY_PURCHASE')}
                        indentLevel="2"
                        isGrey
                      />
                      <RevenueGridItem
                        item={revenueItemsMap.get('RE44')}
                        label={t('MONTHLY_REVENUE_REPORT_TABLE_COMPENSATION_ENERGY_SALE')}
                        contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_COMPENSATION_ENERGY_SALE')}
                        indentLevel="2"
                      />
                    </Collapse>
                  </Collapse>
                </>
              ) : (
                <Collapse in={openSections.RE30} className="col-span-5">
                  <div className="col-span-full grid border-collapse grid-cols-5 gap-0">
                    <RevenueGridItem
                      item={revenueItemsMap.get('RE04')}
                      label={t('MONTHLY_REVENUE_REPORT_TABLE_SCHEDULE_FORECAST')}
                      contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_SCHEDULE_FORECAST')}
                      indentLevel="1.5"
                      isGrey={!hasIntradayTrading}
                    />
                    <RevenueGridItem
                      item={revenueItemsMap.get('RE05')}
                      label={t('MONTHLY_REVENUE_REPORT_TABLE_SCHEDULE_MEASUREMENT')}
                      contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_SCHEDULE_MEASUREMENT')}
                      icon={setArrowIcon(openSections.RE05, technologies)}
                      indentLevel="1.5"
                      variant="body3"
                      isGrey={hasIntradayTrading}
                      onClick={() => setOpenSections({ ...openSections, RE05: !openSections.RE05 })}
                    />
                    <Collapse in={openSections.RE05} className="col-span-5">
                      <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
                        <RevenueGridItem
                          item={revenueItemsMap.get('RE06')}
                          label={t('MONTHLY_REVENUE_REPORT_TABLE_DELIVERY_VALUE_DELIVERED')}
                          contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_DELIVERY_VALUE_DELIVERED')}
                          indentLevel="2"
                          isGrey={!hasIntradayTrading}
                        />
                        <RevenueGridItem
                          item={revenueItemsMap.get('RE07')}
                          label={t('MONTHLY_REVENUE_REPORT_TABLE_DELIVERY_VALUE_DEVIATION')}
                          contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_DELIVERY_VALUE_DEVIATION')}
                          icon={setArrowIcon(openSections.RE07, technologies)}
                          indentLevel="2"
                          variant="body3"
                          isGrey={hasIntradayTrading}
                          onClick={() => setOpenSections({ ...openSections, RE07: !openSections.RE07 })}
                        />
                        <Collapse in={openSections.RE07} className="col-span-5">
                          <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
                            <RevenueGridItem
                              item={revenueItemsMap.get('RE08')}
                              label={t('MONTHLY_REVENUE_REPORT_TABLE_DELIVERY_VALUE_EXCESS')}
                              contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_DELIVERY_VALUE_EXCESS')}
                              indentLevel="2.5"
                              isGrey={!hasIntradayTrading}
                            />

                            <RevenueGridItem
                              item={revenueItemsMap.get('RE09')}
                              label={t('MONTHLY_REVENUE_REPORT_TABLE_DELIVERY_VALUE_SHORTFALL')}
                              contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_DELIVERY_VALUE_SHORTFALL')}
                              icon={setArrowIcon(openSections.RE09, technologies)}
                              indentLevel="2.5"
                              variant="body3"
                              isGrey={hasIntradayTrading}
                              onClick={() => setOpenSections({ ...openSections, RE09: !openSections.RE09 })}
                            />
                            <Collapse in={openSections.RE09} className="col-span-5">
                              <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
                                <RevenueGridItem
                                  item={revenueItemsMap.get('RE10')}
                                  label={t('MONTHLY_REVENUE_REPORT_TABLE_DELIVERY_VALUE_NOT_DELIVERED')}
                                  contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_DELIVERY_VALUE_NOT_DELIVERED')}
                                  indentLevel="3"
                                  isGrey={!hasIntradayTrading}
                                />
                                <RevenueGridItem
                                  item={revenueItemsMap.get('RE11')}
                                  label={t('MONTHLY_REVENUE_REPORT_TABLE_DELIVERY_VALUE_REPLACEMENT')}
                                  contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_DELIVERY_VALUE_REPLACEMENT')}
                                  indentLevel="3"
                                  isGrey={hasIntradayTrading}
                                />
                              </div>
                            </Collapse>

                            <RevenueGridItem
                              label={t('MONTHLY_REVENUE_REPORT_TABLE_BALANCE_COMPENSATION')}
                              icon={setArrowIcon(openSections.RE12, technologies)}
                              variant="body3"
                              indentLevel="2.5"
                              isGrey={!hasIntradayTrading}
                              onClick={() => setOpenSections({ ...openSections, RE12: !openSections.RE12 })}
                            />

                            <Collapse in={openSections.RE12} className="col-span-5">
                              <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
                                <RevenueGridItem
                                  item={revenueItemsMap.get('RE12')}
                                  label={t('MONTHLY_REVENUE_REPORT_TABLE_BALANCE_COMPENSATION_CONTROL_ENERGY')}
                                  contextInfo={t(
                                    'MONTHLY_REVENUE_REPORT_TABLE_HELP_BALANCE_COMPENSATION_CONTROL_ENERGY',
                                  )}
                                  indentLevel="3"
                                  isGrey={hasIntradayTrading}
                                />

                                <RevenueGridItem
                                  item={revenueItemsMap.get('RE13')}
                                  label={t('MONTHLY_REVENUE_REPORT_TABLE_BALANCE_COMPENSATION_REDISPATCH_POS')}
                                  contextInfo={t(
                                    'MONTHLY_REVENUE_REPORT_TABLE_HELP_BALANCE_COMPENSATION_REDISPATCH_POS',
                                  )}
                                  indentLevel="3"
                                  isGrey={!hasIntradayTrading}
                                />

                                <RevenueGridItem
                                  item={revenueItemsMap.get('RE14')}
                                  label={t('MONTHLY_REVENUE_REPORT_TABLE_BALANCE_COMPENSATION_REDISPATCH_NEG')}
                                  contextInfo={t(
                                    'MONTHLY_REVENUE_REPORT_TABLE_HELP_BALANCE_COMPENSATION_REDISPATCH_NEG',
                                  )}
                                  indentLevel="3"
                                  isGrey={hasIntradayTrading}
                                />
                              </div>
                            </Collapse>
                          </div>
                        </Collapse>
                      </div>
                    </Collapse>
                  </div>
                </Collapse>
              )}

              <RevenueGridItem
                item={revenueItemsMap.get('RE34')}
                label={t('REVENUE_REPORT_TABLE_TRADING_COST')}
                contextInfo={t('REVENUE_REPORT_TABLE_HELP_TRADING_COST')}
                icon={setArrowIcon(openSections.RE34, technologies)}
                indentLevel="1"
                variant="body3"
                isGrey={!hasIntradayTrading || isBattery(technologies)}
                onClick={() => setOpenSections({ ...openSections, RE34: !openSections.RE34 })}
              />

              <Collapse in={openSections.RE34} className="col-span-5">
                {!isCoLocation(technologies) && (
                  <RevenueGridItem
                    item={revenueItemsMap.get('RE15')}
                    label={t('REVENUE_REPORT_TABLE_TRADING_COST_MARKET_ACCESS')}
                    contextInfo={t('REVENUE_REPORT_TABLE_HELP_TRADING_COST_MARKET_ACCESS')}
                    indentLevel="1.5"
                    isGrey={hasIntradayTrading && !isBattery(technologies)}
                  />
                )}
                {(isBattery(technologies) || isCoLocation(technologies)) && (
                  <RevenueGridItem
                    item={revenueItemsMap.get('RE45')}
                    label={t('REVENUE_REPORT_TABLE_TRADING_COST_INTRADAY_ORDER')}
                    contextInfo={t('REVENUE_REPORT_TABLE_HELP_TRADING_COST_INTRADAY_ORDER')}
                    indentLevel="1.5"
                    isGrey={isBattery(technologies)}
                  />
                )}
              </Collapse>

              {!isBattery(technologies) && !isCoLocation(technologies) && (
                <>
                  <RevenueGridItem
                    item={revenueItemsMap.get('RE26')}
                    label={t('MONTHLY_REVENUE_REPORT_TABLE_RESULT')}
                    indentLevel="1"
                    variant="body3"
                    isGrey={!hasIntradayTrading}
                  />

                  <RevenueGridItem
                    item={revenueItemsMap.get('RE35')}
                    label={t('REVENUE_REPORT_TABLE_CONTRACT_PRICE_SEPARATE')}
                    contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTRACT_PRICE')}
                    indentLevel="1"
                    variant="body3"
                    isGrey={hasIntradayTrading}
                  />

                  <RevenueGridItem
                    item={revenueItemsMap.get('RE36')}
                    label={t('REVENUE_REPORT_TABLE_REDISPATCH_SEPARATE')}
                    contextInfo={t('REVENUE_REPORT_TABLE_HELP_REDISPATCH')}
                    indentLevel="1"
                    variant="body3"
                    isGrey={!hasIntradayTrading}
                  />

                  <RevenueGridItem
                    item={revenueItemsMap.get('RE32')}
                    label={t('MONTHLY_REVENUE_REPORT_TABLE_RESULT_SCHEDULE_MANAGEMENT')}
                    indentLevel="1"
                    variant="body3"
                    isGrey={hasIntradayTrading}
                  />
                </>
              )}
            </div>
          </Collapse>
        </div>
      )}
      {!isSolar(technologies) && !isCoLocation(technologies) && (
        <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
          <RevenueGridItem
            item={revenueItemsMap.get('RE16')}
            label={t('SHARED_PRODUCT_CONTROL_ENERGY')}
            contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY')}
            icon={setArrowIcon(openSections.RE16, technologies)}
            variant="h5"
            onClick={() =>
              setOpenSections({ ...openSections, RE16: isCogeneration(technologies) || !openSections.RE16 })
            }
          />
        </div>
      )}
      {!isSolar(technologies) && !isCoLocation(technologies) && (
        <Collapse in={openSections.RE16} className="col-span-5">
          <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
            <RevenueGridItem
              item={revenueItemsMap.get('RE17')}
              label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_FCR_LP')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_FCR_LP')}
              indentLevel="1"
              isGrey
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RE18')}
              label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_AFRR_POS_LP')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_AFRR_POS_LP')}
              indentLevel="1"
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RE19')}
              label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_AFRR_POS_AP')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_AFRR_POS_AP')}
              indentLevel="1"
              isGrey
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RE20')}
              label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_AFRR_NEG_LP')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_AFRR_NEG_LP')}
              indentLevel="1"
            />
            <RevenueGridItem
              item={revenueItemsMap.get('RE21')}
              label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_AFRR_NEG_AP')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_AFRR_NEG_AP')}
              indentLevel="1"
              isGrey
            />
            {!isBattery(technologies) && (
              <>
                <RevenueGridItem
                  item={revenueItemsMap.get('RE22')}
                  label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_MFRR_POS_LP')}
                  contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_MFRR_POS_LP')}
                  indentLevel="1"
                />
                <RevenueGridItem
                  item={revenueItemsMap.get('RE23')}
                  label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_MFRR_POS_AP')}
                  contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_MFRR_POS_AP')}
                  indentLevel="1"
                  isGrey
                />
                <RevenueGridItem
                  item={revenueItemsMap.get('RE24')}
                  label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_MFRR_NEG_LP')}
                  contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_MFRR_NEG_LP')}
                  indentLevel="1"
                />
                <RevenueGridItem
                  item={revenueItemsMap.get('RE25')}
                  label={t('MONTHLY_REVENUE_REPORT_TABLE_CONTROL_ENERGY_MFRR_NEG_AP')}
                  contextInfo={t('REVENUE_REPORT_TABLE_HELP_CONTROL_ENERGY_MFRR_NEG_AP')}
                  indentLevel="1"
                  isGrey
                />
              </>
            )}
          </div>
        </Collapse>
      )}
      {!isCogeneration(technologies) && !isSolar(technologies) && (
        <>
          {hasIntradayTrading && !isBattery(technologies) && !isCoLocation(technologies) && (
            <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
              <RevenueGridItem
                item={revenueItemsMap.get('RE37')}
                label={t('MONTHLY_REVENUE_REPORT_TABLE_INTRADAY_TRADING')}
                contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_INTRADAY_TRADING')}
                icon={setArrowIcon(openSections.RE37, technologies)}
                variant="h5"
                onClick={() => setOpenSections({ ...openSections, RE37: !openSections.RE37 })}
              />
              <Collapse in={openSections.RE37} className="col-span-5">
                <RevenueGridItem
                  item={revenueItemsMap.get('RE38')}
                  label={t('MONTHLY_REVENUE_REPORT_TABLE_INTRADAY_TRADING_PURCHASE')}
                  contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_INTRADAY_TRADING_PURCHASE')}
                  indentLevel="1"
                  isGrey
                />
                <RevenueGridItem
                  item={revenueItemsMap.get('RE39')}
                  label={t('MONTHLY_REVENUE_REPORT_TABLE_INTRADAY_TRADING_SALE')}
                  contextInfo={t('MONTHLY_REVENUE_REPORT_TABLE_HELP_INTRADAY_TRADING_SALE')}
                  indentLevel="1"
                />
              </Collapse>
            </div>
          )}
          <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
            <RevenueGridItem
              item={revenueItemsMap.get('RE27')}
              label={t('REVENUE_REPORT_TABLE_EXTRA_PROFIT')}
              contextInfo={t('REVENUE_REPORT_TABLE_HELP_EXTRA_PROFIT')}
              icon={setArrowIcon(openSections.RE27, technologies)}
              variant="h5"
              onClick={() => setOpenSections({ ...openSections, RE27: !openSections.RE27 })}
            />
          </div>
        </>
      )}
      {!isCogeneration(technologies) && !isSolar(technologies) && (
        <Collapse in={openSections.RE27} className="col-span-5">
          <div className="col-span-5 grid border-collapse grid-cols-5 gap-0">
            <RevenueGridItem
              item={revenueItemsMap.get('RE28')}
              label={t('REVENUE_REPORT_TABLE_PROPORTIONAL_PROFIT')}
              contextInfo={
                <Trans
                  components={{
                    h4: <h4 className="text-sx font-semibold mb-0">{}</h4>,
                    i: <i />,
                  }}
                >
                  REVENUE_REPORT_TABLE_HELP_PROPORTIONAL_PROFIT
                </Trans>
              }
              indentLevel="1"
              isGrey
            />
          </div>
        </Collapse>
      )}
      <div data-testid="revenue-report-disclaimer" className="p-4">
        {!isCogeneration(technologies) && !isSolar(technologies) && (
          <>
            {!isBattery(technologies) && (
              <Typography paragraph variant="body2">
                {t('REVENUE_REPORT_TABLE_DISCLAIMER_NOTICE_ONE')}
              </Typography>
            )}
            <Typography paragraph variant="body2">
              {t('REVENUE_REPORT_TABLE_DISCLAIMER_NOTICE_TWO')}
            </Typography>
          </>
        )}
        <Typography paragraph variant="body2">
          {t(
            isSolar(technologies)
              ? 'REVENUE_REPORT_TABLE_DISCLAIMER_SOLAR'
              : isCogeneration(technologies)
                ? 'REVENUE_REPORT_TABLE_DISCLAIMER_CONTROL_ENERGY'
                : 'REVENUE_REPORT_TABLE_DISCLAIMER',
          )}
        </Typography>
      </div>
    </div>
  );
}
